import type { PromoSummary } from '#root/api/clients/cart/data-contracts'
import type { ApproachingDiscount, ApproachingPromotion } from '#types/cart'

export const useApproachingPromotionsStore = defineStore('approachingPromotions', () => {
  const { $t } = useNuxtApp()
  const approachingPromotions = ref<ApproachingPromotion[]>([])

  const splitMessages = (calloutMessage?: string): string[] => {
    if (!calloutMessage) return []
    // the callout message property is expected to have messaging for all stages split by pipe character "|"
    return calloutMessage?.split('|').map((msg: string) => msg.trim()) || []
  }

  const reset = () => {
    approachingPromotions.value = []
  }

  const isGiftWithPurchasePromotion = (promo: ApproachingDiscount | PromoSummary) => promo.promotionId.includes('GWP')

  const getGiftWithPurchaseMessage = (promo: ApproachingDiscount) => {
    if (promo.promotionId.includes('GUEST')) {
      return $t.approachingGiftWithProduct.guest
    }
    else if (promo.promotionId.includes('REGISTERED')) {
      return $t.approachingGiftWithProduct.registered
    }
    else {
      return replaceAll($t.approachingGiftWithProduct.default, {
        distanceFromConditionThreshold: useFormattedPrice(promo.distanceFromConditionThreshold, useCurrencyCode())
      })
    }
  }

  const collectApproachingPromotions = (approachingDiscounts: ApproachingDiscount[] = []) => {
    // process not yet applied promos with unmet activation criteria
    approachingDiscounts.forEach((promo) => {
      const basePromotion: ApproachingPromotion = {
        contexts: promo.approachingDisplayLevel || [],
        promotionClass: promo.promotionClass,
        thresholdStatus: promo.distanceFromConditionThreshold ? 'Above Threshold' : 'Below Threshold',
        distanceFromConditionThreshold: promo.distanceFromConditionThreshold,
        conditionThreshold: promo.conditionThreshold,
        id: promo.promotionId,
        message: '',
      }

      if (isGiftWithPurchasePromotion(promo)) {
        basePromotion.message = getGiftWithPurchaseMessage(promo)
        if (basePromotion.message) {
          approachingPromotions.value.push(basePromotion)
        }
      }
      else {
        const messages = splitMessages(promo.calloutMsg)
        if (messages.length < 3) return // skip if promotion is misconfigured
        basePromotion.message = promo.distanceFromConditionThreshold && messages[1]
          ? replaceAll(messages[1], {
              distanceFromConditionThreshold: useFormattedPrice(promo.distanceFromConditionThreshold, useCurrencyCode())
            })
          : messages[0] || ''
        approachingPromotions.value.push(basePromotion)
      }
    })
  }

  const collectAppliedPromotions = (promoSummary: PromoSummary[] = []) => {
    promoSummary.forEach((promo) => {
      const basePromotion: ApproachingPromotion = {
        contexts: promo.approachingDisplayLevel || [],
        id: promo.promotionId,
        promotionClass: promo.level,
        thresholdStatus: 'Threshold Reached',
        applied: true,
        message: '',
      }
      if (isGiftWithPurchasePromotion(promo)) {
        approachingPromotions.value.push({
          ...basePromotion,
          message: $t.approachingGiftWithProduct.earned,
        })
      }
      else {
        if (!promo?.calloutMessage) return
        const messages = splitMessages(promo.calloutMessage)
        if (messages.length !== 3) return // skip if promotion is misconfigured
        approachingPromotions.value.push({
          ...basePromotion,
          message: messages[2] || ''
        })
      }
    })
  }

  const process = ({ approachingDiscount, promoSummary }: {
    approachingDiscount: ApproachingDiscount[]
    promoSummary: PromoSummary[]
  }) => {
    reset()
    collectApproachingPromotions(approachingDiscount)
    collectAppliedPromotions(promoSummary)
  }

  const get = (context?: ApproachingDiscount['approachingDisplayLevel'][number]) => {
    return context
      ? approachingPromotions.value.filter(({ contexts }) => !contexts.length || contexts.includes(context))
      : approachingPromotions.value
  }

  const dismiss = (id: string) => {
    const promo = approachingPromotions.value.find((promo) => promo.id === id)
    if (!promo) return
    approachingPromotions.value.splice(approachingPromotions.value.indexOf(promo), 1)
  }

  return {
    dismiss,
    get,
    process,
    reset,
    splitMessages,
  }
})
